import React from "react";

import { css } from "@emotion/react";

import { ComponentRenderer } from "../../_layout-helpers/component-renderer";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { StoryLayoutTags } from "./story-layout-tags";
import { StoryLayoutHeadlineLarge } from "./story-layout-headline-large";
import { HeaderSpacer } from "../../_layout-helpers/spacers";
import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { PageMetadataComponent } from "../../../components/PageMetadataComponent/PageMetadataComponent";
import { HeroImageComponent } from "../../../components/HeroImageComponent/HeroImageComponent";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { StoryLayoutReadingTime } from "./story-layout-reading-time";
import { CSS_MQ } from "../../../styles/breakpoints";
import { ExtractSwarovskiPageData, StoryPageData } from "@swo/storefront";
import { ImageComponent } from "../../../components/ImageComponent/ImageComponent";
import { useStore } from "../../../store/hooks/use-store";
import { t2 } from "../../../domain/translations/translations";

export const StoryLayout: React.FC<ExtractSwarovskiPageData<StoryPageData>> = props => {
  const { locationOrigin } = useStore().state;
  const gt = t2(props.generalTranslations);

  const shouldUseReducedSpacerBeforeFooter = props.data.components
    .slice(-1)
    .flatMap(c => (typeof c === "string" ? [] : [c]))
    .map(c => c.__typename === "ComponentImage" || c.__typename === "ComponentImageGallery")
    .every(Boolean);

  return (
    <>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        fallbackImageContentfulUrl={props.data.image.image.url}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />

      {props.data.storyType === "Big Story" ? <BigStoryLayout {...props} /> : <NewsStoryLayout {...props} />}

      {/* Spacing should be already provided by StoryLayout here*/}

      <ComponentRenderer
        components={props.data.components}
        localeForNumberFormat={props.locale}
        translations={gt}
        noSpaceForFirstComponent={true}
      />

      <Spacer size={shouldUseReducedSpacerBeforeFooter ? "sm" : "md"} />
      <FooterComponent {...props.data.footer} />
    </>
  );
};

const BigStoryLayout = (props: ExtractSwarovskiPageData<StoryPageData>) => (
  <>
    <HeaderComponent header={props.data.header} version={"normal"} />
    <HeroImageComponent
      __typename={"ComponentHeroImage"}
      sys={{ id: "" }}
      seoHeadline={props.data.seoHeadline}
      emoHeadline={props.data.emotionalHeadline}
      text={undefined}
      desktopVisualAsset={props.data.image}
      mobileVisualAsset={props.data.image}
      callToAction={undefined}
      switcher={undefined}
      toggle={undefined}
      layout={"switcher-on-hero-image"}
      headlineStyle={props.data.headlineStyle}
      isFirstHeroComponentOnPage={true}
    />
    <Spacer size={"md"} />
    <ContentContainer
      attachStyles={css`
        display: flex;
        flex-wrap: wrap;

        justify-content: center;

        position: relative;
      `}
    >
      <StoryLayoutTags tags={props.data.tags} />
      <StoryLayoutReadingTime
        readingTime={props.data.readingTime}
        attachStyles={css`
          ${CSS_MQ.atLeastMd} {
            position: absolute;
            right: 0;
          }
        `}
      />
    </ContentContainer>
    <Spacer size={"md"} />
  </>
);

const NewsStoryLayout = (props: ExtractSwarovskiPageData<StoryPageData>) => (
  <>
    <HeaderComponent header={props.data.header} version={"normal"} />
    <HeaderSpacer />
    <Spacer size={"md"} />
    <ContentContainer
      attachStyles={css`
        display: flex;
        flex-wrap: wrap;
      `}
    >
      <StoryLayoutTags tags={props.data.tags} />
      <StoryLayoutReadingTime readingTime={props.data.readingTime} />
    </ContentContainer>
    <Spacer size={"md"} />

    <StoryLayoutHeadlineLarge
      emoHeadline={props.data.emotionalHeadline}
      seoHeadline={props.data.seoHeadline}
      alignment={props.data.alignHeadlines}
    />
    <Spacer size={"md"} />
    <ImageComponent
      __typename={"ComponentImage"}
      sys={{ id: "" }}
      image={props.data.image.image}
      width={"full"}
      variant={"fill"}
    />
    <Spacer size={"lg"} />
  </>
);
